import { useEffect } from "react"
import { ActionButton, BaseTable, Button, HeaderMenu, Input, Loading, NumberInput, Select } from "components"
import { Controller, FormProvider, useFieldArray, useForm, useFormContext, useWatch } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import toast from "react-hot-toast"
import { Link, useLocation, useNavigate } from "react-router-dom"
import type { SelectOptionType } from "types"
import { headerLevelList, useApi } from "utils"
import { statusList } from "../utils"
import { type FormType, chequeList, useList, validationSchema } from "./utils"

export default function AccountImportReview(): JSX.Element {
  // Types
  interface Type {
    state?: {
      file_name: string
      status: number
    }
  }

  // Hooks
  const api = useApi()
  const navigate = useNavigate()

  // Form
  const defaultValues: FormType = {
    detail: []
  }
  const methods = useForm<FormType>({
    defaultValues,
    resolver: yupResolver(validationSchema)
  })
  const { control } = methods
  const { fields, remove, replace } = useFieldArray({
    control,
    name: "detail"
  })

  // Hooks
  const location: Type = useLocation()
  
  // Vars
  const importLink: string = "/setup/accounts/account-import"
  const state = location.state
  const isEnabled: boolean = state?.status === 1

  const { dataType, dataLocation, isLoading, refetch } = useList(state?.file_name, state?.status)

  // Form
  const onSubmit = (value: FormType): Promise<void> => {
    // Vars
    const filteredDetail = value.detail.map(item => {
      // Vars
      const { group_coa_id, status, ...rest } = item

      return {
        ...rest,
        account_type_id: item.account_type_id !== "" ? item.account_type_id : undefined,
        coa_cheque: item.coa_cheque === "true" ? true : false
      }
    })

    return new Promise<void>((resolve) => {
      toast.promise(
        api.post(
          "/import_coa/posting_coa_import",
          filteredDetail,
          { params: { file_name: state?.file_name } }
        ),
        {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.message ?? err.response.message
        },
        { duration: 10000 }
      ).then(() => {
        navigate(importLink)
      }).catch(() => {
      }).finally(() => {
        resolve()
      })
    })
  }

  useEffect(() => {
    refetch().then(replace)
    // eslint-disable-next-line
  }, [])

  function deleteSingleRow(preview_id: number): Promise<void> {
    return new Promise<void>((resolve) => {
      toast.promise(
        api.delete(
          "/import_coa/delete_single_row",
          { params: { preview_id } }
        ),
        {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.message ?? err.response.message
        }
      ).then(() => {
        resolve()
      }).catch(() => {
      })
    })
  }

  return (
    <section className="container gap-5 flex flex-col">
      <HeaderMenu title="REVIEW ACCOUNT IMPORT" />

      <div className="overflow-auto max-h-[calc(100svh-370px)]">
        <BaseTable>
          <thead className='bg-[#EEEEEE] sticky top-0 z-[1]'>
            <tr>
              <th>NO</th>
              <th className="w-[90px]">CHQ</th>
              <th className="w-[150px]">ACCOUNT</th>
              <th>NAME</th>
              <th className="w-[100px]">LEVEL</th>
              <th className="w-[180px]">HEADER</th>
              <th>ACCOUNT TYPE</th>
              <th>LOCATION</th>
              <th>STATUS</th>
              <th>ACTION</th>
            </tr>
          </thead>

          <tbody>
            {isLoading && !fields.length ? (
              <tr>
                <td colSpan={10}>
                  <Loading
                    loading={isLoading}
                    errorText="No data available"
                  />
                </td>
              </tr>
            ) : fields.map((field, key) => {
              // Vars
              const group_coa_id = field.group_coa_id
              const account_type_id = `detail.${key}.account_type_id`
              const header_level = `detail.${key}.header_level`

              return (
                <tr key={field.id}>
                  <td className="!align-middle text-center">{key + 1}</td>

                  <td>
                    <Controller
                      control={control}
                      name={`detail.${key}.coa_cheque`}
                      render={({ field, fieldState }) => (
                        <Select
                          error={fieldState.error?.message}
                          controlClass="w-[90px]"
                          isDisabled={!isEnabled}
                          options={chequeList}
                          value={chequeList.find(item => item.value === field.value) ?? null}
                          ref={field.ref}
                          onChange={res => {
                            if (res) {
                              field.onChange(res.value)
                            }
                          }}
                        />
                      )}
                    />
                  </td>

                  <td>
                    <Controller
                      control={control}
                      name={`detail.${key}.coa_code`}
                      render={({ field, fieldState }) => (
                        <Input
                          {...field}
                          inputClass="w-[150px]"
                          error={fieldState.error?.message}
                          disabled={!isEnabled}
                        />
                      )}
                    />
                  </td>

                  <td>
                    <Controller
                      control={control}
                      name={`detail.${key}.coa_name`}
                      render={({ field, fieldState }) => (
                        <Input
                          {...field}
                          error={fieldState.error?.message}
                          disabled={!isEnabled}
                          onBlur={() => field.onChange(field.value.toUpperCase())}
                        />
                      )}
                    />
                  </td>

                  <td >
                    <Controller
                      control={control}
                      name={`detail.${key}.coa_level`}
                      render={({ field, fieldState }) => (
                        <NumberInput
                          error={fieldState.error?.message}
                          disabled={!isEnabled}
                          value={field.value}
                          ref={field.ref}
                          onValueChange={res => field.onChange(res.floatValue)}
                          className="!w-full"
                          inputClass="w-[70px]"
                        />
                      )}
                    />
                  </td>

                  <td>
                    <Controller
                      control={control}
                      name={header_level as "detail.0.header_level"}
                      render={({ field, fieldState }) => (
                        <Select
                          error={fieldState.error?.message}
                          isDisabled={!isEnabled}
                          controlClass="w-[180px]"
                          options={headerLevelList}
                          value={headerLevelList.find(item => item.value === field.value) ?? null}
                          ref={field.ref}
                          onChange={res => {
                            if (res) {
                              field.onChange(res.value)

                              if (res.value === "1") {
                                methods.setValue(account_type_id as "detail.0.account_type_id", "")
                              }
                            }
                          }}
                        />
                      )}
                    />
                  </td>

                  <td>
                    <FormProvider {...methods}>
                      <Type
                        disabled={!isEnabled}
                        filteredOptions={dataType.filter(item => item.group_coa_id === group_coa_id)}
                        headerName={header_level}
                        name={account_type_id}
                      />
                    </FormProvider>
                  </td>

                  <td>
                    <Controller
                      control={control}
                      name={`detail.${key}.location_id`}
                      render={({ field, fieldState }) => (
                        <Select
                          error={fieldState.error?.message}
                          isDisabled={!isEnabled}
                          options={dataLocation}
                          value={dataLocation.find(item => item.value === field.value) ?? null}
                          ref={field.ref} 
                          onChange={res => {
                            if (res) {
                              field.onChange(res.value)
                            }
                          }}
                        />
                      )}
                    />
                  </td>

                  <td className="!align-middle text-center uppercase">{statusList.find(item => item.value === field.status.toString())?.label}</td>

                  <td className="!align-middle text-center">
                    {isEnabled && Boolean(field.status === 1) && (
                      <ActionButton.Delete
                        onClick={() => {
                          deleteSingleRow(
                            field.preview_id
                          ).then(() => {
                            remove(key)
                          })
                        }}
                      />
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </BaseTable>
      </div>

      <section className="flex justify-end gap-3">
        <Link to={importLink}>
          <Button color="error">CANCEL</Button>
        </Link>

        {isEnabled && <Button
          color="primary"
          loading={methods.formState.isSubmitting ? "true" : undefined}
          onClick={methods.handleSubmit(onSubmit)}
        >
          SUBMIT
        </Button>}
      </section>
    </section>
  )
}

function Type(params: {
  disabled: boolean
  filteredOptions: SelectOptionType[]
  headerName: string
  name: string
}): JSX.Element {
  // Vars
  const filteredOptions = params.filteredOptions
  console.log(filteredOptions)

  // Form
  const { control } = useFormContext<FormType>()
  const header_level = useWatch({
    control,
    name: params.headerName as "detail.0.header_level"
  })

  return (
    <Controller
      control={control}
      name={params.name as "detail.0.account_type_id"}
      render={({ field, fieldState }) => (
        <Select
          placeholder="Select Type"
          isDisabled={header_level === "1" || params.disabled}
          error={fieldState.error?.message}
          options={filteredOptions}
          value={filteredOptions.find(item => item.value === field.value) ?? null}
          ref={field.ref}
          onChange={res => {
            if (res) {
              field.onChange(res.value)
            }
          }}
        />
      )}
    />
  )
}